"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonLoading,
  IonToast,
  useIonViewWillEnter,
} from "@ionic/react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Container,
  Stack,
  Autocomplete,
  Divider,
  Avatar,
} from "@mui/material"
import {
  Add,
  Delete,
  ExpandMore,
  Inventory,
  ShoppingCart,
  Cancel,
  CheckCircle,
  Pending,
  Warning,
  Refresh,
  Receipt,
  Event,
  Person,
  Phone,
  Badge,
  Storefront,
  People,
  Group,
  Loyalty,
  Email,
  Business,
  AttachMoney,
  ShoppingBag,
  TrendingUp,
  AccountBalance,
  MonetizationOn,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"

// Update the API imports according to your actual API structure
import { getCustomerStatusReport, getCustomerOrderDetails } from "../../../api/reports/reports"
import { getTlocations_production_centre } from "../../../api/stock-transfer/stock-transfer"
import { getAllCustomers2, getCustomers, getCustomersProfitReport } from "../../../api/customers/customers"

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  border: "1px solid rgba(0,0,0,0.06)",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
  },
}))

const HeaderCard = styled(Box)(({ theme }) => ({
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  padding: theme.spacing(3),
  borderRadius: "12px 12px 0 0",
}))

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  textAlign: "center",
  borderRadius: 8,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
}))

const ProfitCard = styled(Paper)(({ theme, color = "primary" }: any) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  background: `linear-gradient(135deg, ${theme.palette[color].light}15, ${theme.palette[color].light}30)`,
  border: `1px solid ${theme.palette[color].light}40`,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: 80,
    height: 80,
    background: `linear-gradient(135deg, ${theme.palette[color].main}20, transparent)`,
    borderRadius: "0 0 0 80px",
  },
}))

interface CustomerProfit {
  user_id: string
  firstName: string
  lastName: string
  phone: string
  email: string
  roleID: number
  business_id: string
  total_sales: string
  total_cost: string
  total_profit: string
}

interface CustomerOrder {
  id: string
  order_id: string
  customer_id: string
  customer_name: string
  customer_phone: string
  customer_email: string
  branch_id: string
  created_at: string
  total_amount: string
  status: string
  items_count: number
  last_order_date: string
}

const CustomerProfitReportPage: React.FC = () => {
  const [customers, setCustomers] = useState<CustomerProfit[]>([])
  const [allCustomers, setAllCustomers] = useState<CustomerProfit[]>([]) // Store all customers for stats
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null)
  const [startDate, setStartDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null)
  const [customerOrders, setCustomerOrders] = useState<CustomerOrder[] | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [selectedOrderDetails, setSelectedOrderDetails] = useState<any | null>(null)
  const [branches, setBranches] = useState<any[]>([])
  const [statusFilter, setStatusFilter] = useState<string>("all")

  useEffect(() => {
    fetchBranches()
  }, [])

  useIonViewWillEnter(() => {
    fetchBranches()
  })

  const fetchBranches = async () => {
    try {
      const response = await getTlocations_production_centre()
      console.log(response)
      setBranches(response.data.data || [])
    } catch (error) {
      console.log(error)
      setToastMessage("Failed to fetch branches")
      setShowToast(true)
    }
  }

  const fetchCustomerProfitData = async () => {
    if (!selectedBranch || !startDate || !endDate) {
      setToastMessage("Please select a branch and a date range.")
      setShowToast(true)
      return
    }
    setLoading(true)
    try {
      const response = await getCustomersProfitReport({
        branch_id: selectedBranch,
        startDate,
        endDate,
      })
      console.log("Customer profit response:", response)

      // Handle different possible response structures
      const customerData = response.data || response || []
      
      // Store all customers for statistics
      setAllCustomers(customerData)
      
      // Filter out customers with total sales <= 0
      const filteredCustomers = customerData.filter(
        (customer: CustomerProfit) => {
          const totalSales = parseFloat(customer.total_sales || '0');
          return totalSales > 0;
        }
      )
      
      setCustomers(filteredCustomers)
      
      if (filteredCustomers.length === 0) {
        setToastMessage("No profitable data found for the selected criteria.")
        setShowToast(true)
      } else if (filteredCustomers.length < customerData.length) {
        const filteredOutCount = customerData.length - filteredCustomers.length;
        setToastMessage(`Showing ${filteredCustomers.length} of ${customerData.length} customers (${filteredOutCount} customers with zero or negative sales filtered out).`)
        setShowToast(true)
      }
    } catch (error) {
      console.log(error)
      setToastMessage("Failed to fetch customer profit data")
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const fetchCustomerOrders = async (customerId: string) => {
    if (!startDate || !endDate) {
      setToastMessage("Please select a date range first.")
      setShowToast(true)
      return
    }
    
    setLoading(true)
    setCustomerOrders(null)
    try {
      const response = await getCustomerStatusReport({
        customer_id: customerId,
        startDate,
        endDate,
        branch_id: "all",
        status: statusFilter !== "all" ? statusFilter : undefined,
      })
      console.log(response);

      setCustomerOrders(response.data)
      if (response.data.length === 0) {
        setToastMessage("No orders found for this customer in the selected period.")
        setShowToast(true)
      }
    } catch (error) {
      console.error("Error fetching customer orders:", error)
      setToastMessage("Failed to fetch customer orders.")
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleViewCustomerOrders = (customer: CustomerProfit) => {
    setSelectedCustomer(customer.user_id)
    fetchCustomerOrders(customer.user_id)
  }

  const handleViewOrderDetails = async (order_id: string) => {
    setDetailsOpen(true)
    setDetailsLoading(true)
    try {
      const response = await getCustomerOrderDetails({
        order_id,
        customer_id: selectedCustomer!,
      })
      console.log(response);
      setSelectedOrderDetails(response)
    } catch (error) {
      console.error("Error fetching order details:", error)
      setToastMessage("Failed to fetch order details.")
      setShowToast(true)
    } finally {
      setDetailsLoading(false)
    }
  }

  // Calculate totals for the summary cards based on profit data
  const calculateTotals = () => {
    const totalCustomers = customers?.length || 0;
    const totalAllCustomers = allCustomers?.length || 0;
    const totalSales = customers?.reduce((acc, customer) => acc + parseFloat(customer.total_sales || '0'), 0) || 0;
    const totalCost = customers?.reduce((acc, customer) => acc + parseFloat(customer.total_cost || '0'), 0) || 0;
    const totalProfit = customers?.reduce((acc, customer) => acc + parseFloat(customer.total_profit || '0'), 0) || 0;
    const averageProfitMargin = totalSales > 0 ? ((totalProfit / totalSales) * 100) : 0;
    
    return { 
      totalCustomers, 
      totalAllCustomers,
      totalSales, 
      totalCost, 
      totalProfit, 
      averageProfitMargin 
    };
  };

  const totals = calculateTotals();

  const getCustomerDisplayName = (customer: CustomerProfit) => {
    if (customer.firstName && customer.lastName) {
      return `${customer.firstName} ${customer.lastName}`;
    }
    return customer.firstName || customer.email || customer.phone || "Unknown Customer";
  };

  const getInitials = (customer: CustomerProfit) => {
    const name = getCustomerDisplayName(customer);
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getProfitMargin = (sales: string, cost: string) => {
    const salesNum = parseFloat(sales);
    const costNum = parseFloat(cost);
    if (salesNum === 0) return 0;
    return ((salesNum - costNum) / salesNum) * 100;
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `₦${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <IonPage className="pushDown">
      <IonHeader className="ion-no-border ">
        <IonToolbar>
          <IonTitle>Customer Profit Report</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <Container maxWidth="lg" sx={{ py: 2 }}>
          {/* Profit Summary Cards */}
          <StyledCard sx={{ mb: 3 }}>
            <HeaderCard>
              <Typography
                variant="h6"
                fontWeight="600"
                sx={{ display: "flex", alignItems: "center", gap: 1, color: "#1e293b" }}
              >
                Generate Customer Profit Report
              </Typography>
            </HeaderCard>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Select Branch</InputLabel>
                    <Select
                      value={selectedBranch || ""}
                      onChange={(e) => setSelectedBranch(e.target.value)}
                      label="Select Branch"
                    >
                      <MenuItem value="all">All Branches</MenuItem>
                      {branches.map((branch) => (
                        <MenuItem key={branch.branch_id} value={branch.branch_id}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate || ""}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate || ""}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={fetchCustomerProfitData}
                    disabled={!startDate || !endDate || !selectedBranch || loading}
                    sx={{
                      py: 1.5,
                      bgcolor: "#1e293b",
                      "&:hover": { bgcolor: "#334155" },
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Generate"}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>
          {customers.length > 0 && (
            <>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={3}>
                  <ProfitCard color="primary">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <People sx={{ fontSize: 40, color: "primary.main" }} />
                      <Box>
                        <Typography variant="h6" color="text.secondary">
                          Active Customers
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="primary.main">
                          {totals.totalCustomers}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          of {totals.totalAllCustomers} total
                        </Typography>
                      </Box>
                    </Box>
                  </ProfitCard>
                </Grid>
                <Grid item xs={12} md={3}>
                  <ProfitCard color="warning">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <AccountBalance sx={{ fontSize: 40, color: "warning.main" }} />
                      <Box>
                        <Typography variant="h6" color="text.secondary">
                          Total Sales
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="warning.main">
                          {formatCurrency(totals.totalSales)}
                        </Typography>
                      </Box>
                    </Box>
                  </ProfitCard>
                </Grid>
                <Grid item xs={12} md={3}>
                  <ProfitCard color="error">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <ShoppingCart sx={{ fontSize: 40, color: "error.main" }} />
                      <Box>
                        <Typography variant="h6" color="text.secondary">
                          Total Cost
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="error.main">
                          {formatCurrency(totals.totalCost)}
                        </Typography>
                      </Box>
                    </Box>
                  </ProfitCard>
                </Grid>
                <Grid item xs={12} md={3}>
                  <ProfitCard color="success">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TrendingUp sx={{ fontSize: 40, color: "success.main" }} />
                      <Box>
                        <Typography variant="h6" color="text.secondary">
                          Total Profit
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="success.main">
                          {formatCurrency(totals.totalProfit)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Margin: {totals.averageProfitMargin.toFixed(1)}%
                        </Typography>
                      </Box>
                    </Box>
                  </ProfitCard>
                </Grid>
              </Grid>

              {/* Customer Profit Table */}
              <StyledCard>
                <HeaderCard>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="600" color="#1e293b">
                      Customer Profit Analysis
                    </Typography>
                    <Box>
                      <Chip 
                        label={`${customers.length} profitable customers`}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                      {totals.totalAllCustomers > totals.totalCustomers && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({totals.totalAllCustomers - totals.totalCustomers} zero-sales customers filtered)
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </HeaderCard>
                <CardContent>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Customer</TableCell>
                          <TableCell>Contact</TableCell>
                          <TableCell align="right">Total Sales</TableCell>
                          <TableCell align="right">Total Cost</TableCell>
                          <TableCell align="right">Total Profit</TableCell>
                          <TableCell align="right">Profit Margin</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customers.map((customer) => {
                          const profitMargin = getProfitMargin(customer.total_sales, customer.total_cost);
                          return (
                            <TableRow key={customer.user_id} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                                    {getInitials(customer)}
                                  </Avatar>
                                  <Box>
                                    <Typography fontWeight="500">
                                      {getCustomerDisplayName(customer)}
                                    </Typography>
                                    {customer.roleID && (
                                      <Chip 
                                        label={`Role: ${customer.roleID}`} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ mt: 0.5 }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Stack spacing={0.5}>
                                  {customer.phone && customer.phone !== "1" && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Phone fontSize="small" sx={{ color: "#64748b" }} />
                                      <Typography variant="body2">{customer.phone}</Typography>
                                    </Box>
                                  )}
                                  {customer.email && customer.email !== "1" && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Email fontSize="small" sx={{ color: "#64748b" }} />
                                      <Typography variant="body2">{customer.email}</Typography>
                                    </Box>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body1" fontWeight="600" color="warning.main">
                                  {formatCurrency(customer.total_sales)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body1" fontWeight="600" color="error.main">
                                  {formatCurrency(customer.total_cost)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body1" fontWeight="600" color="success.main">
                                  {formatCurrency(customer.total_profit)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip 
                                  label={`${profitMargin.toFixed(1)}%`}
                                  color={
                                    profitMargin > 30 ? "success" :
                                    profitMargin > 15 ? "warning" : "error"
                                  }
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </StyledCard>

              {/* Customer Orders Section */}
              {customerOrders && selectedCustomer && (
                <StyledCard sx={{ mt: 3 }}>
                  <HeaderCard>
                    <Typography variant="h6" fontWeight="600" color="#1e293b">
                      Orders for {getCustomerDisplayName(customers.find(c => c.user_id === selectedCustomer)!)}
                    </Typography>
                  </HeaderCard>
                  <CardContent>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6} md={3}>
                        <StatsCard>
                          <Typography variant="h6">Total Orders</Typography>
                          <Typography variant="h4" fontWeight="bold" color="primary.main">
                            {customerOrders.length}
                          </Typography>
                        </StatsCard>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <StatsCard>
                          <Typography variant="h6">Total Revenue</Typography>
                          <Typography variant="h4" fontWeight="bold" color="success.main">
                            {formatCurrency(customerOrders.reduce((acc, order) => acc + parseFloat(order.total_amount), 0))}
                          </Typography>
                        </StatsCard>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <StatsCard>
                          <Typography variant="h6">Completed</Typography>
                          <Typography variant="h4" fontWeight="bold" color="success.main">
                            {customerOrders.filter(order => order.status === "COMPLETED").length}
                          </Typography>
                        </StatsCard>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <StatsCard>
                          <Typography variant="h6">Pending</Typography>
                          <Typography variant="h4" fontWeight="bold" color="warning.main">
                            {customerOrders.filter(order => order.status === "PENDING").length}
                          </Typography>
                        </StatsCard>
                      </Grid>
                    </Grid>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Order ID</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Branch</TableCell>
                            <TableCell align="right">Items</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {customerOrders.map((order) => (
                            <TableRow key={order.id}>
                              <TableCell>{order.order_id}</TableCell>
                              <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                              <TableCell>
                                {branches.find(b => b.branch_id === order.branch_id)?.name || order.branch_id}
                              </TableCell>
                              <TableCell align="right">{order.items_count}</TableCell>
                              <TableCell align="right">{formatCurrency(order.total_amount)}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={order.status} 
                                  color={
                                    order.status === "COMPLETED" ? "success" :
                                    order.status === "PENDING" ? "warning" :
                                    order.status === "CANCELLED" ? "error" : "default"
                                  } 
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleViewOrderDetails(order.order_id)}
                                >
                                  View Details
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </StyledCard>
              )}
            </>
          )}
        </Container>

        {/* Order Details Dialog */}
        <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #e2e8f0" }}>
            <Receipt sx={{ color: "#64748b" }} />
            Order Details
          </DialogTitle>
          <DialogContent sx={{ bgcolor: "#f8fafc", p: 3 }}>
            {detailsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                <CircularProgress />
              </Box>
            ) : selectedOrderDetails ? (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#1e293b" }}>
                    Order Information
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Event fontSize="small" sx={{ color: "#64748b" }} />
                      <Typography variant="body2" color="text.secondary">
                        Order Date:{" "}
                        <Typography component="span" fontWeight="500" color="text.primary">
                          {new Date(selectedOrderDetails.created_at).toLocaleString()}
                        </Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Storefront fontSize="small" sx={{ color: "#64748b" }} />
                      <Typography variant="body2" color="text.secondary">
                        Branch:{" "}
                        <Typography component="span" fontWeight="500" color="text.primary">
                          {branches.find(b => b.branch_id === selectedOrderDetails.branch_id)?.name || selectedOrderDetails.branch_id}
                        </Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={selectedOrderDetails.status}
                        color={
                          selectedOrderDetails.status === "COMPLETED" ? "success" :
                          selectedOrderDetails.status === "PENDING" ? "warning" :
                          selectedOrderDetails.status === "CANCELLED" ? "error" : "default"
                        }
                        size="small"
                      />
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#1e293b" }}>
                    Order Summary
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight="600" color="primary.main" sx={{ textAlign: 'center' }}>
                      Total: {formatCurrency(selectedOrderDetails.total_amount)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              <Typography sx={{ p: 4, textAlign: "center" }}>No details found.</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: "1px solid #e2e8f0" }}>
            <Button onClick={() => setDetailsOpen(false)} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <IonLoading isOpen={loading} message="Processing..." />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="top"
        />
      </IonContent>
    </IonPage>
  )
}

export default CustomerProfitReportPage